import { useEffect, useState } from "react";
import type { Session } from "./data/session";
import { usingEmulators } from "./firebase";
import { useRulesProbe } from "./useBackendStatus";

/**
 * The step-1 scaffold proof, folded away.
 *
 * These rows are how a failing environment explains itself instead of showing a
 * blank screen, and they are what the Playwright smoke test asserts on. Kept
 * collapsed so they cost the user nothing, and dev-only so they never ship.
 */
export function Diagnostics({ session }: { session: Session }) {
  const rules = useRulesProbe();
  const [origin, setOrigin] = useState("");

  useEffect(() => setOrigin(window.location.host), []);

  if (!import.meta.env.DEV) return null;

  const auth =
    session.state === "signed-in"
      ? { state: "ok" as const, detail: `signed in (${session.uid.slice(0, 6)}…)` }
      : session.state === "error"
        ? { state: "bad" as const, detail: session.message }
        : { state: "pending" as const, detail: "connecting…" };

  return (
    <details className="diagnostics">
      <summary>Dev diagnostics</summary>
      <div className="panel">
        <Row label="PWA" value="booted" state="ok" />
        <Row label="Backend" value={usingEmulators ? "emulator" : "cloud"} state="ok" />
        <Row label="Auth" value={auth.detail} state={auth.state} />
        <Row label="Firestore" value={rules.detail} state={rules.state} />
        <Row label="Origin" value={origin} state="ok" />
      </div>
    </details>
  );
}

function Row({
  label,
  value,
  state,
}: {
  label: string;
  value: string;
  state: "ok" | "pending" | "bad";
}) {
  return (
    <div className="row">
      <span className="label">{label}</span>
      <span className={`value ${state}`} data-testid={`status-${label.toLowerCase()}`}>
        {value}
      </span>
    </div>
  );
}
